/**
 * Print orchestration for the Recurring transactions list.
 *
 * Responsibilities (recurring-specific):
 *   - Convert RecurringTemplate rows into escaped HTML cells.
 *   - Build the letterhead data from the company name string.
 *   - Call buildPrintHtml (from printTemplate.ts) with everything.
 *   - Open a new window, write the HTML, trigger print.
 *
 * The HTML template itself lives in printTemplate.ts and is
 * reusable for other reports (Rules, Bank transactions, Invoices).
 * This file contains the recurring-specific pieces only.
 */

import type { RecurringTemplate } from "../recurringTypes";
import { COLUMNS, type ColumnKey } from "../recurringColumns";
import { MODE_LABELS, TXN_TYPE_LABELS } from "../recurringMockData";
import {
  formatAmount,
  formatDateLong,
  formatIntervalLabel,
  getPartyLabel,
} from "../recurringUtils";
import {
  buildPrintHtml,
  escapeHtml,
  type LetterheadData,
} from "./printTemplate";

/**
 * Returns an HTML-escaped cell value for the given template and
 * column key. Status overrides for next date ("Ended", "Paused")
 * stay unescaped text since they're hard-coded.
 */
export function printCellContent(
  t: RecurringTemplate,
  key: ColumnKey,
): string {
  switch (key) {
    case "name":
      return escapeHtml(t.name);
    case "type":
      return escapeHtml(MODE_LABELS[t.mode]);
    case "txnType":
      return escapeHtml(TXN_TYPE_LABELS[t.txnType]);
    case "interval": {
      const i = formatIntervalLabel(t.schedule);
      return escapeHtml(
        i.secondary ? `${i.primary} (${i.secondary})` : i.primary,
      );
    }
    case "previous":
      return escapeHtml(formatDateLong(t.previousDate));
    case "next": {
      if (t.status === "ended") return "Ended";
      if (t.status === "paused") return "Paused";
      return escapeHtml(formatDateLong(t.nextDate));
    }
    case "party":
      return escapeHtml(getPartyLabel(t));
    case "amount":
      return escapeHtml(formatAmount(t.transaction.amount));
    default:
      return "";
  }
}

/**
 * Opens a new window containing the printable recurring list and
 * triggers the browser print dialog. The new window auto-closes
 * after the print dialog is dismissed (onafterprint handler).
 *
 * companyName empty hides the entire left letterhead block (no
 * placeholder text). When Settings or the backend fills in company
 * data later, the block appears automatically.
 */
export function printRecurringList(
  templates: RecurringTemplate[],
  viewModeLabel: string,
  companyName: string,
  visibleColumns: ReadonlySet<ColumnKey>,
): void {
  const generatedAt = new Date().toLocaleString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const printableCols = COLUMNS.filter(
    (c) => visibleColumns.has(c.key) && c.key !== "action",
  );

  const rows = templates
    .map((t) => {
      const cells = printableCols
        .map((c) => {
          const content = printCellContent(t, c.key);
          const cls =
            c.key === "name"
              ? ' class="name-cell"'
              : c.rightAlign
                ? ' class="amount-cell"'
                : "";
          return `<td${cls}>${content}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const countLabel = `${templates.length} ${templates.length === 1 ? "template" : "templates"}`;

  const letterhead: LetterheadData = {
    companyName,
    // address1, address2, cityLine, phone, email will arrive once
    // Settings or the backend provides them. For now only the name
    // (if present) renders in the letterhead block.
  };

  const html = buildPrintHtml({
    reportTitle: "Recurring transactions",
    viewModeLabel,
    countLabel,
    generatedAt,
    letterhead,
    visibleColumns,
    rows,
  });

  const w = window.open("", "_blank", "width=1200,height=800");
  if (!w) {
    alert("Allow popups for this site to print the list.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
}